/* File : src/components/guest/AgeVerification.js */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../common/Button';
import { publicApi } from '../../services/api';
import './AgeVerification.css';
import backgroundImage from './background.webp';

/**
 * Composant pour vérifier l'âge des utilisateurs (21 ans minimum)
 */
const AgeVerification = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [birthDate, setBirthDate] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  
  // Gestionnaire de changement pour le champ de date de naissance
  const handleDateChange = (e) => {
    const input = e.target.value.replace(/\D/g, ''); // Garder seulement les chiffres
    
    let formattedDate = '';
    
    if (input.length > 0) {
      // Formater le jour (maximum 31)
      const day = input.substring(0, 2);
      const numericDay = parseInt(day);
      
      if (day.length === 2 && (numericDay < 1 || numericDay > 31)) {
        setError('Jour invalide');
        return;
      }
      
      formattedDate = day;
      
      // Ajouter la barre oblique et le mois
      if (input.length > 2) {
        formattedDate += '/' + input.substring(2, 4);
        const numericMonth = parseInt(input.substring(2, 4));
        
        if (input.length >= 4 && (numericMonth < 1 || numericMonth > 12)) {
          setError('Mois invalide');
          return;
        }
        
        // Ajouter la barre oblique et l'année
        if (input.length > 4) {
          formattedDate += '/' + input.substring(4, 8);
        }
      }
    }
    
    setBirthDate(formattedDate);
    setError('');
  };
  
  // Position automatique du curseur après le dernier caractère
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.setSelectionRange(birthDate.length, birthDate.length);
    }
  }, [birthDate]);
  
  // Auto-focus sur le séparateur "/", permettant de continuer la saisie
  useEffect(() => {
    if (birthDate.length === 2 || birthDate.length === 5) {
      if (inputRef.current) {
        setTimeout(() => {
          inputRef.current.setSelectionRange(birthDate.length + 1, birthDate.length + 1);
        }, 10);
      }
    }
  }, [birthDate]);
  
  const handleVerifyAge = async () => {
    // Vérification de base
    if (!birthDate || birthDate.length < 10) {
      setError('Veuillez entrer une date de naissance complète (JJ/MM/AAAA)');
      return;
    }
    
    // Extraire les composants de la date
    const parts = birthDate.split('/');
    if (parts.length !== 3) {
      setError('Format de date invalide');
      return;
    }
    
    const day = parseInt(parts[0]);
    const month = parseInt(parts[1]);
    const year = parseInt(parts[2]);
    
    // Vérifier la validité de la date
    if (isNaN(day) || isNaN(month) || isNaN(year) || 
        day < 1 || day > 31 || month < 1 || month > 12 || 
        year < 1900 || year > new Date().getFullYear()) {
      setError('Date de naissance invalide');
      return;
    }
    
    // Vérifier si la date est valide (ex: 31 février n'existe pas)
    const testDate = new Date(year, month - 1, day);
    if (testDate.getDate() !== day || testDate.getMonth() !== month - 1 || testDate.getFullYear() !== year) {
      setError('Date de naissance invalide');
      return;
    }
    
    setLoading(true);
    
    try {
      // Calculer l'âge
      const birthDateObj = new Date(year, month - 1, day);
      const today = new Date();
      let age = today.getFullYear() - birthDateObj.getFullYear();
      const m = today.getMonth() - birthDateObj.getMonth();
      
      if (m < 0 || (m === 0 && today.getDate() < birthDateObj.getDate())) {
        age--;
      }
      
      if (age < 21) {
        setError('Vous devez avoir au moins 21 ans pour accéder à ce site');
        setLoading(false);
        return;
      }
      
      // Vérification côté serveur via l'API
      const response = await publicApi.verifyAge({
        day,
        month,
        year
      });
	  
	  console.log("Réponse complète:", response);
		console.log("eligible:", response.data.eligible);

		if (response.data && response.data.eligible === true) {
		  console.log("Utilisateur éligible, sauvegarde...");
		  
		  try {
			localStorage.setItem('birthDate', birthDate);
			localStorage.setItem('birthDay', day);
			localStorage.setItem('birthMonth', month);
			localStorage.setItem('birthYear', year);

			console.log("Redirection lancée");
			navigate('/city-selection', { replace: true });
		  } catch (storageError) {
			console.error("Erreur localStorage", storageError);
			window.location.href = '/brightbar/city-selection';
		  }
		}
    } catch (err) {
      console.error('Error verifying age:', err);
      setError('Une erreur est survenue. Veuillez réessayer plus tard.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAlreadyRegistered = () => {
    // Vérifier si une ville est déjà sélectionnée
    const selectedCity = localStorage.getItem('selectedCity');
    if (selectedCity) {
      navigate('/retrieve-ticket');
    } else {
      // Si pas de ville, aller d'abord à la sélection de ville
      navigate('/city-selection');
    }
  };
  
  return (
    <div className="age-verification-container" style={{ backgroundImage: `url(${backgroundImage})` }}>
      <div className="age-verification-logo">
        <img src="https://guinnessevents.cm/brightbar/assets/images/guinness-logo.png" alt="Guinness Cameroun" />
      </div>
      
      <div className="age-verification-content">
        <h1>Bienvenue à la Guinness Bright Bar Experience</h1>
        <h2>Préparez vous à vivre quelque chose d'extraordinaire.</h2>
        <p>Veuillez confirmer votre âge pour continuer</p>
        
        <div className="age-verification-form">
          <div className="age-verification-input-container">
            <input 
              ref={inputRef}
              type="text"
              className={`age-verification-input ${error ? 'age-verification-error' : ''}`}
              value={birthDate}
              onChange={handleDateChange}
              placeholder="JJ/MM/AAAA"
              maxLength={10}
              disabled={loading}
            />
            
            {error && (
              <div className="age-verification-error-message">{error}</div>
            )}
          </div>
          
          <Button 
            variant="guinness"
            size="lg"
            fullWidth
            onClick={handleVerifyAge}
            disabled={loading}
          >
            {loading ? 'Vérification...' : 'Continuer'}
          </Button>
          
          <div className="age-verification-already-registered">
            <button 
              className="age-verification-text-button"
              onClick={handleAlreadyRegistered}
              disabled={loading}
            >
              Déjà inscrit ? Retrouver mon pass
            </button>
          </div>
        </div>
      </div>
      
      <div className="age-verification-disclaimer">
        <div className="age-restriction">
          <span className="age-restriction-circle">21+</span>
          <p>NE PAS TRANSFERER AUX PERSONNES AGEES DE MOINS DE 21 ANS</p>
        </div>
        <p>A CONSOMMER AVEC MODERATION</p>
        <p className="drinkiq">www.drinkiq.com</p>
      </div>
    </div>
  );
};

export default AgeVerification;